import { Button, Chip, FieldErrors, Label, Typography } from '@stern-ui/ui';
import { AnyFieldMeta } from '@tanstack/react-form';
import { useEffect, useState } from 'react';

import { useValidatePatientIdsMutation } from './api';

export type PatientIdValidationProps = {
	name: string;
	label: string;
	value: string[];
	onValueChange: (newValue: string[]) => void;
	fieldStateMeta: AnyFieldMeta;
	dataTestId?: string;
};

export const PatientIdValidation = (props: PatientIdValidationProps) => {
	const { label, name, value: fieldValue, onValueChange, fieldStateMeta, dataTestId } = props;

	const [inputValue, setInputValue] = useState<string>(''); // Value entered in the text area
	const [invalidValues, setInvalidValues] = useState<string[]>([]); // Invalid values returned by the API
	const [isUpdating, setIsUpdating] = useState<boolean>(false); // Flag to check if an update to the values has been triggered

	const [
		validatePatientIds,
		{ data: validatePatientIdData, isLoading: validatePatientIdLoading },
	] = useValidatePatientIdsMutation();

	const inputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
		setInputValue(e.target.value); // Update the input value state when a user enters a value
	};

	const handleValidate = () => {
		// Clean the input value and split it by space into an array and filter out empty strings
		validatePatientIds(
			inputValue
				.replace(/[^a-zA-Z0-9\s-]/g, '')
				.split(/\s+/)
				.filter((val) => val.length > 0)
		);
	};

	useEffect(() => {
		if (validatePatientIdData) {
			// If the API call returns data then update state/form field with valid and invalid values, and remove the data from the input field
			const { valid: validPatientIds, invalid: invalidPatientIds } = validatePatientIdData;
			setIsUpdating(true);
			onValueChange(validPatientIds);
			setInvalidValues(invalidPatientIds);
			setInputValue('');
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [validatePatientIdData]);

	useEffect(() => {
		// listen for changes to fieldValue. This is to handle updates to the value outside of the component (e.g. 'Clear' button)
		// only reset invalid values if not isUpdating so ensure invalid chips are displayed when there are only invalid values
		if (fieldValue?.length === 0 && !isUpdating) {
			setInvalidValues([]);
		}
		setIsUpdating(false);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [fieldValue]);

	const handleValidRemove = (value: string) => {
		// set isUpdating to true to prevent the invalid values from being cleared if all valid values are removed
		setIsUpdating(true);
		onValueChange(fieldValue.filter((val: string) => val !== value));
	};

	const hideErrors = !fieldStateMeta?.isTouched || !fieldStateMeta?.isBlurred;

	return (
		<>
			<Label htmlFor={name} label={label} className='mb-2' />
			<div className='flex flex-row' data-test-id={`${dataTestId}-input`}>
				<textarea
					id={name}
					onChange={inputChange}
					className='flex-1 min-w-0 block w-full p-3 mr-8 text-base rounded-md border border-primaryBorder placeholder:text-base placeholder:text-disabledText'
					value={inputValue}
				/>
				<div className='flex flex-col justify-end'>
					<Button
						variant='secondary'
						onClick={() => handleValidate()}
						disabled={!inputValue || validatePatientIdLoading}
						isLoading={validatePatientIdLoading}
						data-test-id={`${dataTestId}-button`}
					>
						Validate
					</Button>
				</div>
			</div>
			{invalidValues.length > 0 && (
				<div className='pt-8' data-test-id={`${dataTestId}-invalid`}>
					<Typography variant='bodySmallBold'>
						Invalid patient IDs, please correct them in order for them to be included in
						the filters:
					</Typography>
					<div className='flex flex-wrap gap-4 mt-4'>
						{invalidValues.map((value, i) => (
							<Chip
								key={i}
								value={value}
								style='invalid'
								onClose={() =>
									setInvalidValues(invalidValues.filter((val) => val !== value))
								}
							/>
						))}
					</div>
				</div>
			)}
			{fieldValue?.length > 0 && (
				<div className='pt-8' data-test-id={`${dataTestId}-valid`}>
					<Typography variant='bodySmallBold'>Valid patient IDs</Typography>
					<div className='flex flex-wrap gap-4 mt-4'>
						{fieldValue.map((value: string, i: number) => (
							<Chip
								key={i}
								value={value}
								style='valid'
								onClose={() => handleValidRemove(value)}
							/>
						))}
					</div>
				</div>
			)}
			<FieldErrors meta={fieldStateMeta} name={name} hideErrors={hideErrors} />
		</>
	);
};
